import * as React from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Breadcrumbs,
  Button,
  Autocomplete,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import theme from "../../../theme";
import { Dayjs } from "dayjs";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Pagination from "@mui/material/Pagination";
import { ReturnAndClaim } from "../../../interfaces/core/ReturnAndClaim";
import {
  getReturnRequests,
  updateReturnRequestStatus,
  updateReturnRequestVisited,
} from "../../../services/core-api";
import { ReturnItem } from "../../../interfaces/core/ReturnItem";
import { UpdateReturnAndClaim } from "../../../interfaces/core/UpdateReturnAndClaim";

function Row(props: { row: ReturnAndClaim }) {
  const baseURL = process.env.REACT_APP_API_URL;
  const { row } = props;
  const [open, setOpen] = React.useState(false);

  const handleExportPdf = (id: number) => {
    const url = `${baseURL}/return-claim/${id}/export`;
    window.open(url);
  };

  const handleReadOrderStatus = async (row: UpdateReturnAndClaim) => {
    setOpen(!open);
    if (!row.visited) {
      try {
        const response = await updateReturnRequestVisited(row);
        if (response) {
          row.visited = !row.visited;
        }
      } catch (error) {}
    }
  };

  const handleStatusChange = async (
    event: React.ChangeEvent<{}>,
    newStatus: string | null
  ) => {
    if (newStatus && newStatus !== row.status) {
      try {
        // Call your updateOrderStatus function
        const updatedRequest = await updateReturnRequestStatus(
          { id: row.id },
          newStatus
        );
        // Update the status in your row if the request was successful
        row.status = updatedRequest.status;
        window.location.reload();
      } catch (error) {}
    }
  };

  const date = new Date(row.created_at);
  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ padding: "5px" }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => handleReadOrderStatus(row)}
          >
            {open ? (
              <KeyboardArrowUpIcon sx={{ color: "#F37C2E" }} />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </TableCell>
        <TableCell align="left" 
          sx={{ padding: "5px", fontWeight: !row.visited ? 'bold' : 'normal', cursor: "pointer" }}
          onClick={() => handleReadOrderStatus(row)}
          >
          {row.id}
        </TableCell>
        <TableCell align="center" 
          sx={{ padding: "5px", fontWeight: !row.visited ? 'bold' : 'normal', cursor: "pointer" }}
          onClick={() => handleReadOrderStatus(row)}
          >
          {row.user.zone}
        </TableCell>
        <TableCell align="center" 
          sx={{ padding: "5px", fontWeight: !row.visited ? 'bold' : 'normal', cursor: "pointer" }}
          onClick={() => handleReadOrderStatus(row)}
          >
          {`${formattedDate} ${formattedTime}`}
        </TableCell>
        <TableCell align="right" 
          sx={{ padding: "5px", fontWeight: !row.visited ? 'bold' : 'normal', cursor: "pointer" }}
          onClick={() => handleReadOrderStatus(row)}
        >
          {row.reason}
        </TableCell>
        <TableCell align="right" sx={{ padding: "5px" }}>
          <Button
            sx={{
              color: theme.palette.text.primary,
              fontSize: "13px",
              fontFamily: "Inter",
              border: "1px solid #f1f1f1",
              textTransform: "capitalize",
            }}
            onClick={() => handleExportPdf(row.id)}
          >
            Export PDF
          </Button>
        </TableCell>

        <TableCell
          align="right"
          sx={{
            padding: "5px",
            color: "#2ccb32",
            fontSize: "13px",
            fontFamily: "Inter",
            fontWeight: 500,
          }}
        >
          <Autocomplete
            disablePortal
            id="combo-box-demo"
            options={requestStatuses}
            value={row.status}
            onChange={handleStatusChange}
            sx={{
              bgcolor: "#fff",
              float: "right",
              width: "160px",
              "& .MuiOutlinedInput-root": {
                height: "30px",
                borderRadius: 0,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-input": {
                height: "30px",
                padding: "0 14px",
                fontSize: "13px",
                fontFamily: "Inter",
                color:
                  row.status === "PROCESSING"
                    ? "#00F"
                    : row.status === "ACCEPTED"
                    ? "#2ccb32"
                    : row.status === "REJECTED"
                    ? "#F22"
                    : "#FFB122",
              },
              "& .MuiInputLabel-root": {
                color: "#5d5d5d",
                top: "-10px",
                fontSize: "13px",
                fontFamily: "Inter",
              },
            }}
            renderInput={(params) => <TextField {...params} />}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: '13px', fontFamily: 'Inter', color: '#333' }}>
                {option}
              </li>
            )}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
          }}
          colSpan={12}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1, border: "2px solid #f1f1f1", p: 1 }}>
            <Box sx={{width: '100%'}}>
                <Typography sx={{ml:2, pt:2, mb:1, fontSize: "15px", fontFamily: "Inter", fontWeight: 500}}>
                  Client information
                </Typography>
              </Box>
            <Table size="small" aria-label="purchases">
                <TableHead sx={{ bgcolor: "#f1f1f1", p: 1 }}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      Surname
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      City
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      Country
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontFamily: "Inter", fontWeight: 600 }}
                    >
                      Phone
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontFamily: "Inter", fontWeight: 600 }}
                    >
                      Address
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontFamily: "Inter", fontWeight: 600 }}
                    >
                      Company
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        {row.user.name}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.user.surname}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {row.user.city}
                      </TableCell>
                      <TableCell>
                        {row.user.country}
                      </TableCell>
                      <TableCell align="right">{row.user.phone}</TableCell>
                      <TableCell align="right">{row.user.address}</TableCell>
                      <TableCell align="right">
                        {row.user.company}
                      </TableCell>
                    </TableRow>
                </TableBody>
              </Table>
              <Box sx={{width: '100%'}}>
                <Typography sx={{ml:2, pt:2, mb:1, fontSize: "15px", fontFamily: "Inter", fontWeight: 500}}>
                  Return request Items
                </Typography>
              </Box>
              <Table size="small" aria-label="purchases">
                <TableHead sx={{ bgcolor: "#f1f1f1", p: 1 }}>
                  <TableRow>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      Nr
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      Article number
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Inter", fontWeight: 600 }}>
                      Manufacturer
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontFamily: "Inter", fontWeight: 600 }}
                    >
                      Quantity
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontFamily: "Inter", fontWeight: 600 }}
                    >
                      Unit Price
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.return_items.map((item: ReturnItem) => (
                    <TableRow key={item.id}>
                      <TableCell component="th" scope="row">
                        {item.id}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {item.articleNumber}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {item.mfrName}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{Number(item.unit_price).toFixed(2)} €</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const requestStatuses = ["PROCESSING", "ACCEPTED", "REJECTED"];

const ReturnsAndClaims = () => {
  const [returnAndClaims, setReturnAndClaims] = React.useState<
    ReturnAndClaim[] | []
  >([]);
  const [selectedFromDate, setSelectedFromDate] = React.useState<Dayjs | null>(
    null
  );
  const [selectedToDate, setSelectedToDate] = React.useState<Dayjs | null>(
    null
  );
  const [page, setPage] = React.useState(1);
  const rowsPerPage = 10;
  const [count, setCount] = React.useState(0);

  const fetchReturnRequests = React.useCallback(async () => {
    try {
      const options = {
        from_date: selectedFromDate
          ? selectedFromDate.format("YYYY-MM-DD")
          : "",
        to_date: selectedToDate ? selectedToDate.format("YYYY-MM-DD") : "",
        page_number: page,
      };
      console.log(options);
      const response = await getReturnRequests(options);
      if (response) {
        setReturnAndClaims(response.returnAndClaims);
        setPage(response.page_number);
        setCount(Math.ceil(response.total / rowsPerPage));
      }
    } catch (error) {
      setReturnAndClaims([]);
      setPage(1);
      setCount(0);
    }
  }, [selectedFromDate, selectedToDate, page, rowsPerPage]);

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      fetchReturnRequests();
    }
    return () => {
      isMounted = false;
    };
  }, [fetchReturnRequests]);

  const handleFromDateChange = (date: Dayjs | null) => {
    setSelectedFromDate(date);
    setPage(1); // Reset to first page on date change
  };

  const handleToDateChange = (date: Dayjs | null) => {
    setSelectedToDate(date);
    setPage(1); // Reset to first page on date change
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setPage(page);
    window.scrollTo({
      top: 100,
      left: 100,
      behavior: "smooth",
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <>
        <Box
          sx={{
            flexGrow: 1,
            m: 2,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box
                sx={{
                  bgcolor: "#f1f1f1",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  paddingLeft: 1,
                  borderTop: `2px solid ${theme.palette.primary.dark}`,
                }}
              >
                <Grid container spacing={1}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={7}
                    sx={{ paddingLeft: "0px !important" }}
                  >
                    <Stack
                      spacing={2}
                      sx={{ paddingTop: "3px", paddingLeft: "15px" }}
                    >
                      <Breadcrumbs aria-label="breadcrumb">
                        <Typography
                          key="3"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            fontSize: "15px",
                            fontFamily: "Inter",
                          }}
                        >
                          Return and Claim requests
                        </Typography>
                      </Breadcrumbs>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={12} md={5}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pr: 1,
                      }}
                    >
                      <Box>
                        <DatePicker
                          label="From Date"
                          value={selectedFromDate}
                          onChange={handleFromDateChange}
                          format="MM/DD/YYYY"
                          sx={{
                            bgcolor: "#fff",
                            mr: 1,
                            width: "160px",
                            "& .MuiOutlinedInput-root": {
                              height: "30px",
                              borderRadius: 0,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: 0,
                            },
                            "& .MuiInputBase-input": {
                              height: "30px",
                              padding: "0 14px",
                              fontSize: "13px",
                              fontFamily: "Inter",
                            },
                            "& .MuiInputLabel-root": {
                              color: "#5d5d5d",
                              top: "-10px",
                              fontSize: "13px",
                              fontFamily: "Inter",
                            },
                          }}
                        />
                        <DatePicker
                          label="To Date"
                          value={selectedToDate}
                          onChange={handleToDateChange}
                          format="MM/DD/YYYY"
                          sx={{
                            bgcolor: "#fff",
                            mr: 1,
                            width: "160px",
                            "& .MuiOutlinedInput-root": {
                              height: "30px",
                              borderRadius: 0,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderRadius: 0,
                            },
                            "& .MuiInputBase-input": {
                              height: "30px",
                              padding: "0 14px",
                              fontSize: "13px",
                              fontFamily: "Inter",
                            },
                            "& .MuiInputLabel-root": {
                              color: "#5d5d5d",
                              top: "-10px",
                              fontSize: "13px",
                              fontFamily: "Inter",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ overflowX: "auto" }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    boxShadow: "none",
                    minWidth: "800px",
                    border: "1px solid #f1f1f1",
                    p: 2,
                  }}
                >
                  <Table aria-label="collapsible table">
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 600,
                            pr: 0,
                            pb: 0,
                          }}
                        >
                          Request No.
                        </TableCell>
                        <TableCell
                          sx={{ fontFamily: "Inter", fontWeight: 600, pb: 0 }}
                          align="center"
                        >
                          Zone
                        </TableCell>
                        <TableCell
                          sx={{ fontFamily: "Inter", fontWeight: 600, pb: 0 }}
                          align="center"
                        >
                          Date & Time
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 600,
                            pr: 0,
                            pb: 0,
                          }}
                          align="right"
                        >
                          Reason
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 600,
                            pr: 0,
                            pb: 0,
                          }}
                          align="right"
                        >
                          Document
                        </TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "Inter",
                            fontWeight: 600,
                            pr: 0,
                            pb: 0,
                          }}
                          align="right"
                        >
                          Status Order
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnAndClaims.map((row) => (
                        <Row key={row.id} row={row} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack spacing={2}>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}
                  >
                    <Pagination
                      count={count}
                      page={page}
                      variant="outlined"
                      shape="rounded"
                      onChange={handlePageChange}
                    />
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </>
    </LocalizationProvider>
  );
};
export default ReturnsAndClaims;
